import { Link } from "react-router-dom";
import { Layout } from "@/components/layout";
import { ProtectedRoute } from "@/components/auth";
import { useDashboard } from "@/hooks/useDashboard";
import {
  DashboardHeader,
  DashboardStatsCards,
  PendingConsultationCard,
  EmptyState,
  DashboardFilters,
} from "@/components/dashboard";

export default function Dashboard() {
  const {
    dashboardData,
    filteredConsultations,
    loading,
    error,
    showRecommendations,
    destinationInfoMap,
    loadingDestinations,
    expandedCountries,
    filters,
    handleToggleRecommendations,
    toggleCountryExpansion,
    updateFilter,
    resetFilters,
    getUrgencyLevel,
  } = useDashboard();

  if (loading) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-teal-600 border-t-transparent mb-6"></div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Loading Your Dashboard...
              </h2>
              <p className="text-gray-600">
                Please wait while we gather your consultations
              </p>
            </div>
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  if (error) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
            <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-error-bg rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-error"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Oops! Something Went Wrong
              </h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors duration-200 inline-flex items-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Try Again
              </button>
            </div>
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  // Count consultations for stats cards
  const priorityCount = filteredConsultations.filter(
    (c) =>
      getUrgencyLevel(c.departureDate) === "overdue" ||
      getUrgencyLevel(c.departureDate) === "urgent" ||
      c.priority?.toLowerCase() === "high"
  ).length;

  const pendingCount = filteredConsultations.filter(
    (c) => c.formsStatus.pending > 0
  ).length;

  const approvedCount = filteredConsultations.filter(
    (c) => c.formsStatus.completed > 0
  ).length;

  return (
    <ProtectedRoute>
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-12">
          {dashboardData && (
            <>
              {dashboardData.pendingConsultations &&
              dashboardData.pendingConsultations.length > 0 ? (
                <>
                  {/* Filters Component */}
                  <DashboardFilters
                    consultations={dashboardData.pendingConsultations}
                    filters={filters}
                    onFilterChange={updateFilter}
                    onResetFilters={resetFilters}
                    getUrgencyLevel={getUrgencyLevel}
                  />

                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {filteredConsultations.length > 0 ? (
                      <div className="space-y-4">
                        {filteredConsultations.map((consultation) => (
                          <PendingConsultationCard
                            key={consultation.consultationId}
                            consultation={consultation}
                            showRecommendations={
                              showRecommendations ===
                              consultation.consultationId
                            }
                            loadingDestinations={
                              loadingDestinations[
                                consultation.consultationId
                              ] || false
                            }
                            destinationInfoMap={destinationInfoMap}
                            expandedCountries={expandedCountries}
                            onToggleRecommendations={() =>
                              handleToggleRecommendations(
                                consultation.consultationId,
                                consultation.destinations
                              )
                            }
                            onToggleCountry={toggleCountryExpansion}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
                        <div className="mb-6">
                          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                            <svg
                              className="w-10 h-10 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                          </div>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                          No Consultations Found
                        </h3>
                        <p className="text-gray-600 mb-6 text-lg max-w-md mx-auto">
                          We couldn't find any consultations matching your
                          current filter. Try viewing all consultations instead.
                        </p>
                        <button
                          onClick={resetFilters}
                          className="px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg inline-flex items-center gap-2"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                          Show All Consultations
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="bg-white rounded-xl p-16 shadow-sm border border-gray-200 text-center">
                    <div className="mb-6">
                      <div className="w-24 h-24 bg-teal-100 rounded-full flex items-center justify-center mx-auto">
                        <svg
                          className="w-12 h-12 text-teal-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                      All Caught Up!
                    </h3>
                    <p className="text-gray-600 text-lg mb-2">
                      You don't have any consultations to review right now.
                    </p>
                    <p className="text-gray-500">
                      New consultations will appear here when patients submit
                      their forms.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Layout>
    </ProtectedRoute>
  );
}
